'use strict';

import axios from 'axios';
import to from 'await-to-js';
import {
  reportingMaxSelectableAttributes,
  reportingMaxSelectableWorkpackages,
} from '@sharedModules/data/enums/feature-flags';
import { yearly } from '@enums/historical-periods';
import { deserializeReportingSelections } from '../../utils/reporting-url-serializer';
import { formatFilters } from '../utils/filters';

const getInitialState = () => ({
  selectedWorkpackageIds: [],
  selectedWorkpackages: [],
  availableWorkpackages: [],
  selectedAttributeKeys: [],
  selectedAttributes: [],
  selectedAttributeValues: [],
  approvalStatus: 'notApproved',
  availableAttributes: [],
  availableHierarchyFilters: [], // Hierarchy filters with intersected values
  attributeValuesCache: {}, // Cache for attribute values from intersection
  loading: false,
  error: '',
  // Reporting aggregations data for the grid
  reportingAggregations: [],
  // Row expansion state for reporting grid
  // Tracks which hierarchy row is currently expanded with split details (only one at a time)
  // Structure: { levelEntryKey: string, level: number, groupBy: string, selectedOption: string } | null
  // - groupBy can be 'storeGroups' for store group splits, or an attribute key for attribute splits
  // When null, no rows are expanded
  expandedRow: null,
  // Dropdown selection state for reporting grid
  // Tracks which hierarchy row currently has a dropdown option selected (only one at a time)
  // Structure: { levelEntryKey: string, selectedOption: string } | null
  // When null, no dropdown is selected
  selectedHierarchyRow: null,
  splitData: {},
  splitLoading: false,
});

const store = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    selectedWorkpackageNames: state => state.selectedWorkpackages.map(wp => wp.description),
    selectedAttributeDisplayNames: state => state.selectedAttributes.map(a => a.displayDescription),
    contextForView: state => ({
      workpackageIds: state.selectedWorkpackageIds,
      workpackageNames: state.selectedWorkpackages.map(wp => wp.description),
      attributeKeys: state.selectedAttributeKeys,
      attributeDisplayNames: state.selectedAttributes.map(a => a.displayDescription),
      approvalStatus: state.approvalStatus,
    }),

    getAvailableAttributes: state => state.availableAttributes,
    isLoading: state => state.loading,

    // Getter to read cached values
    // Usage: getters['reporting/getAttributeValues']({ workpackageIds, attributeKeys })
    getAttributeValues: state => ({ workpackageIds = [], attributeKeys = [] } = {}) => {
      const result = {};
      (workpackageIds || []).forEach(wpId => {
        const perWp = state.attributeValuesByWpSet[wpId] || {};
        result[wpId] = {};
        (attributeKeys || []).forEach(key => {
          const entry = perWp[key];
          result[wpId][key] = entry ? entry.values : undefined;
        });
      });
      return result;
    },
  },

  mutations: {
    resetState(state) {
      Object.assign(state, getInitialState());
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error || '';
    },
    setReportingAggregations(state, items) {
      state.reportingAggregations = items;
    },
    setAvailableWorkpackages(state, workpackages) {
      state.availableWorkpackages = Array.isArray(workpackages) ? workpackages : [];
    },
    setSelectedWorkpackageIds(state, ids) {
      state.selectedWorkpackageIds = Array.isArray(ids) ? ids : [];
    },
    setSelectedWorkpackages(state, workpackages) {
      state.selectedWorkpackages = Array.isArray(workpackages) ? workpackages : [];
    },
    setSelectedAttributeKeys(state, keys) {
      state.selectedAttributeKeys = Array.isArray(keys) ? keys : [];
    },
    setSelectedAttributes(state, attributes) {
      state.selectedAttributes = Array.isArray(attributes) ? attributes : [];
    },
    setApprovalStatus(state, status) {
      state.approvalStatus = status || 'notApproved';
    },
    setAvailableAttributes(state, attrs) {
      state.availableAttributes = Array.isArray(attrs) ? attrs : [];
    },
    setAvailableHierarchyFilters(state, filters) {
      state.availableHierarchyFilters = Array.isArray(filters) ? filters : [];
    },

    // Cache attribute values from intersection response
    setAttributeValuesCache(state, cache) {
      if (!cache) {
        state.attributeValuesCache = {};
        return;
      }
      state.attributeValuesCache = {
        ...state.attributeValuesCache,
        ...cache,
      };
    },

    // Store raw attribute values payload for selected attributes
    // results: Array<{ wpId: string, attributeKey: string, distinctValues: string[] }>
    setSelectedAttributeValues(state, results) {
      state.selectedAttributeValues = Array.isArray(results) ? results : [];
    },

    // Row expansion mutations (handles both store group and attribute splits)
    setExpandedRow(state, expandedRow) {
      state.expandedRow = expandedRow;
    },

    setSplitData(state, { levelEntryKey, data }) {
      state.splitData = {
        ...state.splitData,
        [levelEntryKey]: data,
      };
    },

    clearSplitData(state, levelEntryKey) {
      if (levelEntryKey) {
        // Clear data for specific levelEntryKey
        const newSplitData = { ...state.splitData };
        delete newSplitData[levelEntryKey];
        state.splitData = newSplitData;
      } else {
        // Clear all split data
        state.splitData = {};
      }
    },

    setSplitLoading(state, loading) {
      state.splitLoading = loading;
    },

    // Dropdown selection mutations
    setSelectedHierarchyRow(state, selectedRow) {
      state.selectedHierarchyRow = selectedRow;
    },
  },

  actions: {
    async fetchIntersectedAttributes(
      { commit, rootState },
      { workpackageIds, includeValues = false, maxAttributeValues } = {}
    ) {
      if (!Array.isArray(workpackageIds) || workpackageIds.length === 0) {
        commit('setAvailableAttributes', []);
        return [];
      }
      commit('setLoading', true);
      try {
        const toggleLogic = rootState.clientConfig.toggleLogic;
        const maxWorkpackages = toggleLogic[reportingMaxSelectableWorkpackages];

        const { data } = await axios.post('/api/attribute-metadata/intersection', {
          workpackageIds,
          includeValues,
          maxWorkpackages,
          maxAttributeValues,
        });

        const attributes = Array.isArray(data) ? data : [];

        // Cache attribute values if included in response
        if (includeValues) {
          const valuesCache = {};
          attributes.forEach(attr => {
            if (attr.distinctValues) {
              valuesCache[attr.key] = attr.distinctValues;
            }
          });
          commit('setAttributeValuesCache', valuesCache);
        }

        commit('setAvailableAttributes', attributes);
        return attributes;
      } catch (e) {
        commit('setError', e && e.message ? e.message : '');
        commit('setAvailableAttributes', []);
        return [];
      } finally {
        commit('setLoading', false);
      }
    },

    // Fetch attribute values and store raw results
    async fetchAttributeValuesByKeys({ commit }, { workpackageIds = [], attributeKeys = [] }) {
      if (!Array.isArray(workpackageIds) || workpackageIds.length === 0) return [];
      if (!Array.isArray(attributeKeys) || attributeKeys.length === 0) return [];
      const { data = [] } = await axios.post('/api/attributes/keys/resolve', {
        workpackageIds,
        attributeKeys,
      });
      commit('setSelectedAttributeValues', data);
      return data;
    },

    // Fetch workpackages for reporting by IDs, or by type. If ids not provided, fetch all.
    async fetchReportingWorkpackages({ commit }, { ids = [], type } = {}) {
      const params = {};
      const conditions = [];

      if (Array.isArray(ids) && ids.length > 0) {
        conditions.push({ _id: { $in: ids } });
      }
      if (type) {
        conditions.push({ type });
      }

      let where = {};
      if (conditions.length > 1) {
        where = { $and: conditions };
      } else if (conditions.length === 1) {
        where = conditions[0];
      }
      params.where = JSON.stringify(where);

      const { data } = await axios.get('/api/work-packages', { params });
      const items = Array.isArray(data) ? data : [];
      if (Array.isArray(ids) && ids.length > 0) {
        commit('setSelectedWorkpackages', items);
      } else {
        commit('setAvailableWorkpackages', items);
      }
      return items;
    },

    async initialiseFromRoute({ commit, dispatch, rootState }, routeQuery) {
      const query = routeQuery || {};

      // Clear any previous row expansion state when reinitialising
      // This ensures stale selections don't persist when report parameters change
      commit('setExpandedRow', null);
      commit('setSelectedHierarchyRow', null);
      commit('clearSplitData');

      const toggleLogic = rootState.clientConfig.toggleLogic;
      const maxWp = toggleLogic[reportingMaxSelectableWorkpackages];
      const maxAttr = toggleLogic[reportingMaxSelectableAttributes];

      // Use URL serialization helper
      const { workpackageIds, attributeKeys, approvalStatus } = deserializeReportingSelections(
        query,
        {
          maxWorkpackages: maxWp,
          maxAttributes: maxAttr,
        }
      );

      commit('setSelectedWorkpackageIds', workpackageIds);
      commit('setSelectedAttributeKeys', attributeKeys);
      commit('setApprovalStatus', approvalStatus);

      // Ensure minimal workpackages list is available via local reporting fetch
      if (workpackageIds.length > 0) {
        await dispatch('fetchReportingWorkpackages', { ids: workpackageIds });
      } else {
        commit('setSelectedWorkpackages', []);
      }

      // Prefill cache of values for selected attributes without running a heavier intersection
      if (workpackageIds.length > 0 && attributeKeys.length > 0) {
        const attributeData = await dispatch('fetchAttributeValuesByKeys', {
          workpackageIds,
          attributeKeys,
        });

        // Populate selectedAttributes for the dropdown
        const attributes = attributeData.map(attr => ({
          key: attr.key,
          displayDescription: attr.label,
        }));
        commit('setSelectedAttributes', attributes);
      }
    },

    // Row expansion actions (handles both store group and attribute splits)
    async toggleRowExpansion(
      { commit, dispatch, state, rootState, rootGetters },
      { levelEntryKey, level, groupBy, selectedOption, workpackageIdForNode }
    ) {
      const currentExpanded = state.expandedRow;

      // If clicking the same option on the same row that's already expanded, collapse it
      if (
        currentExpanded &&
        currentExpanded.levelEntryKey === levelEntryKey &&
        currentExpanded.selectedOption === selectedOption
      ) {
        commit('setExpandedRow', null);
        commit('clearSplitData', levelEntryKey);
        return;
      }

      // Collapse any previously expanded row and clear its data
      if (currentExpanded) {
        commit('clearSplitData', currentExpanded.levelEntryKey);
        commit('setExpandedRow', null);
      }

      // Clear data for the row we're about to expand to prevent showing stale data
      commit('clearSplitData', levelEntryKey);

      // Set new expanded row and fetch data
      const newExpandedRow = {
        levelEntryKey,
        level,
        groupBy,
        selectedOption,
        workpackageIdForNode,
      };
      commit('setExpandedRow', newExpandedRow);

      // Use the same filter logic as the main reporting request
      const { reportingAttributesFilter } = rootState.filters;
      let formattedAttributeFilters = {};
      if (reportingAttributesFilter.length) {
        formattedAttributeFilters = formatFilters({
          where: reportingAttributesFilter,
          rootState,
        });
      }

      await dispatch('fetchSplitData', {
        levelEntryKey,
        hierarchyLevel: level,
        workpackageIds: state.selectedWorkpackageIds,
        workpackageIdForNode,
        competitors: rootGetters['gridView/competitors'] || [],
        attributeFilters: formattedAttributeFilters,
        historicalPeriod: yearly,
        splitBy:
          groupBy === 'storeGroups'
            ? { type: 'storeGroup' }
            : { type: 'attribute', attributeKey: groupBy },
      });
    },

    // Dropdown selection actions
    selectHierarchyRow({ commit, state }, { levelEntryKey, selectedOption }) {
      const currentSelected = state.selectedHierarchyRow;

      // If selecting the same option in the same row, deselect it
      if (
        currentSelected &&
        currentSelected.levelEntryKey === levelEntryKey &&
        currentSelected.selectedOption === selectedOption
      ) {
        commit('setSelectedHierarchyRow', null);
        return;
      }

      // If the row being modified is currently expanded, collapse it
      // because changing the split-by option invalidates the current expansion
      if (state.expandedRow && state.expandedRow.levelEntryKey === levelEntryKey) {
        commit('setExpandedRow', null);
        commit('clearSplitData', levelEntryKey);
      }

      // If selecting a different row or different option, update selection
      const newSelection = { levelEntryKey, selectedOption };
      commit('setSelectedHierarchyRow', newSelection);
    },

    async fetchSplitData({ commit, state }, params) {
      const {
        levelEntryKey,
        hierarchyLevel,
        workpackageIds,
        workpackageIdForNode,
        competitors = [],
        attributeFilters = {},
        historicalPeriod = yearly,
        splitBy = { type: 'storeGroup' },
      } = params;

      commit('setSplitLoading', true);

      try {
        const requestBody = {
          workpackageIds,
          hierarchyId: levelEntryKey,
          hierarchyLevel,
          workpackageIdForNode,
          competitors,
          attributeFilters,
          historicalPeriod,
          approvalStatus: state.approvalStatus || 'notApproved',
          splitBy,
          allowedAttributeKeys: state.selectedAttributeKeys,
        };

        const { data } = await axios.post('/api/reporting/aggregated/details', requestBody);

        // Backend always returns { splitBy, items, divisors, [attributeMeta] }
        const items = data.items || [];

        commit('setSplitData', {
          levelEntryKey,
          data: items,
        });
      } catch (error) {
        console.error('Error fetching split data:', error);
        commit('setError', error.message || 'Failed to fetch split data');
        // Reset the expanded row on error
        commit('setExpandedRow', null);
      } finally {
        commit('setSplitLoading', false);
      }
    },

    async fetchReportingAggregations({ commit, dispatch, rootState, rootGetters, state }) {
      // Format attribute filters for use in both main request and split data refetch
      const reportingAttributesFilter = rootState.filters.reportingAttributesFilter || [];
      let formattedAttributeFilters = {};
      if (reportingAttributesFilter.length) {
        formattedAttributeFilters = formatFilters({
          where: reportingAttributesFilter,
          rootState,
        });
      }

      // Clear split data for expanded row to prevent showing stale data during loading
      if (state.expandedRow) {
        commit('clearSplitData', state.expandedRow.levelEntryKey);
      }

      // Use new dedicated endpoint for all workpackages (1 or more)
      const selectedWorkpackageIds =
        (rootState.reporting && rootState.reporting.selectedWorkpackageIds) || [];

      const response = await dispatch('fetchMultiWorkpackageReportingAggregations', {
        workpackageIds: selectedWorkpackageIds,
      });

      // Backend provides data ready for grid (no transformation needed)
      const items = response.items || [];

      commit('setReportingAggregations', items);

      // If there's an expanded row, check if it still exists in new results
      if (state.expandedRow) {
        const expandedRow = state.expandedRow;
        const parentRowExists = items.some(
          item => item.levelEntryKey === expandedRow.levelEntryKey
        );

        // If parent row no longer exists, collapse the expansion
        if (!parentRowExists) {
          commit('setExpandedRow', null);
        } else {
          // Refetch split data with new filters
          await dispatch('fetchSplitData', {
            levelEntryKey: expandedRow.levelEntryKey,
            hierarchyLevel: expandedRow.level,
            workpackageIds: selectedWorkpackageIds,
            workpackageIdForNode: expandedRow.workpackageIdForNode,
            competitors: rootGetters['gridView/selectedCompetitorKeys'] || [],
            attributeFilters: formattedAttributeFilters, // Use the same formatted filters
            historicalPeriod: yearly,
            splitBy:
              expandedRow.groupBy === 'storeGroups'
                ? { type: 'storeGroup' }
                : { type: 'attribute', attributeKey: expandedRow.groupBy },
          });
        }
      }
    },

    /**
     * PRICE-4991: Fetch reporting aggregations for multiple workpackages.
     * Uses new /api/reporting/aggregated endpoint.
     */
    async fetchMultiWorkpackageReportingAggregations(
      { commit, state, rootState, rootGetters },
      { workpackageIds } = {}
    ) {
      commit('setLoading', true);

      // Format attribute filters for the request
      const reportingAttributesFilter = rootState.filters.reportingAttributesFilter || [];
      let formattedAttributeFilters = {};
      if (reportingAttributesFilter.length) {
        formattedAttributeFilters = formatFilters({
          where: reportingAttributesFilter,
          rootState,
        });
      }

      const params = {
        workpackageIds: JSON.stringify(workpackageIds),
        competitors: JSON.stringify(rootGetters['gridView/selectedCompetitorKeys'] || []),
        attributeFilters: JSON.stringify(formattedAttributeFilters),
        historicalPeriod: yearly,
        approvalStatus: state.approvalStatus || '',
      };

      const headers = { 'refresh-cache': true };

      const [err, response] = await to(
        axios.get('/api/reporting/aggregated', {
          params,
          headers,
        })
      );

      commit('setLoading', false);

      if (err) {
        console.error('Error fetching multi-workpackage reporting aggregations:', err);
        throw err;
      }

      // Don't commit here - let the caller do the transformation and commit
      return response.data;
    },
    /**
     * Clear row expansion state when leaving the reporting page.
     * We do not persist expanded state across navigations.
     * This applies to both store group and attribute split expansions.
     */
    clearRowExpansion({ commit, state }) {
      if (state.expandedRow) {
        commit('clearSplitData', state.expandedRow.levelEntryKey);
      }
      commit('setExpandedRow', null);
    },

    /**
     * Fetch attribute values for a single attribute that are present in ALL selected workpackages (intersection).
     * This is used when a user selects an attribute in the reporting filter.
     */
    async fetchIntersectedAttributeValues({ commit, state }, { workpackageIds, attributeKey }) {
      if (!Array.isArray(workpackageIds) || workpackageIds.length === 0 || !attributeKey) {
        return [];
      }

      commit('attributes/setLoadingAvailableValues', true, { root: true });

      try {
        // Check if we have cached values from the intersection response
        const cachedValues = state.attributeValuesCache[attributeKey];

        let intersectedValuesArray;
        if (cachedValues && Array.isArray(cachedValues)) {
          intersectedValuesArray = cachedValues;
        } else {
          // Fetch values for this specific attribute from all workpackages
          const { data = [] } = await axios.post('/api/attributes/keys/resolve', {
            workpackageIds,
            attributeKeys: [attributeKey],
          });

          // Find values that exist in ALL workpackages (intersection)
          // Group values by workpackage
          const valuesByWorkpackage = [];
          data.forEach(item => {
            if (item.attributeKey === attributeKey && Array.isArray(item.distinctValues)) {
              valuesByWorkpackage.push(new Set(item.distinctValues));
            }
          });

          // If we don't have values from all workpackages, return empty
          if (valuesByWorkpackage.length === 0) {
            intersectedValuesArray = [];
          } else if (valuesByWorkpackage.length === 1) {
            // Only one workpackage has this attribute, use its values
            intersectedValuesArray = Array.from(valuesByWorkpackage[0]);
          } else {
            // Find intersection: values that appear in ALL workpackages
            const firstSet = valuesByWorkpackage[0];
            intersectedValuesArray = Array.from(firstSet).filter(value =>
              valuesByWorkpackage.every(wpValues => wpValues.has(value))
            );
          }

          // Cache the fetched values for future use
          const cacheUpdate = { [attributeKey]: intersectedValuesArray };
          commit('setAttributeValuesCache', cacheUpdate);
        }

        // Transform to the format expected by attribute-filter component
        const availableAttributeValues = intersectedValuesArray.map(value => ({
          attributeKey: value,
          attributeValue: value,
        }));

        // Commit to attributes store (reuse existing mutation)
        commit(
          'attributes/setAvailableAttributeValues',
          {
            selectedAttributeKey: attributeKey,
            availableAttributeValues,
          },
          { root: true }
        );

        return intersectedValuesArray;
      } catch (e) {
        console.error('Error fetching intersected attribute values:', e);
        return [];
      } finally {
        commit('attributes/setLoadingAvailableValues', false, { root: true });
      }
    },
  },
};

export default store;
